<script setup lang="ts">
    import type { FilterOption } from '~/types/filters';

    const props = defineProps<{
        title: string;
        options: FilterOption[];
        selectedOption?: FilterOption;
        translateOptions?: boolean;
    }>();

    const emit = defineEmits(['update:selectedOption']);

    const { t } = useI18n();

    const isActive = ref(false);
    const isOpen = ref(false);
    const hasScrollbar = ref(false);
    const selectedOption = ref(props.selectedOption || props.options[0]);

    const customSelectEl = ref();

    const handleSelectFilter = () => {
        if (selectedOption.value?.key != props.options[0]?.key) {
            isActive.value = true;
        } else {
            isActive.value = false;
        }

        isOpen.value = !isOpen.value;
    };

    const setSelectedFilter = (key: string, label: string) => {
        selectedOption.value = { key, label } as FilterOption;
        emit('update:selectedOption', selectedOption.value);
        handleSelectFilter();
    };

    watch(
        () => customSelectEl.value,
        () => {
            if (customSelectEl.value?.scrollHeight > customSelectEl.value?.clientHeight) {
                hasScrollbar.value = true;
            } else {
                hasScrollbar.value = false;
            }
        }
    );
</script>

<template>
    <button
        class="custom-select-button"
        @click="handleSelectFilter"
    >
        <span class="select-title">{{ title }}</span>

        <div
            class="select-value"
            :class="{ '--open': isOpen }"
        >
            <span
                v-if="!props.translateOptions"
                class="select-value"
            >
                {{ selectedOption.label }}
            </span>
            <span
                v-else
                class="select-value"
            >
                {{ t(`pagePlp.filterValues.bucketKeys.${selectedOption.label}`, selectedOption.label) }}
            </span>
            <img
                class="icon"
                src="~/assets/icons/chevron-right-black.svg"
                alt="Filter"
                aria-hidden="true"
            />
        </div>
    </button>

    <Transition name="filter">
        <div
            v-if="isOpen"
            ref="customSelectEl"
            class="custom-select-modal"
            :class="{ '--has-scrollbar': hasScrollbar }"
        >
            <button
                class="back-button"
                @click="handleSelectFilter"
            >
                <img
                    src="~/assets/icons/chevron-right-black.svg"
                    alt="close"
                />
                <span>{{ t('general.back', 'Zurück') }}</span>
            </button>

            <template
                v-for="{ key, label, secondLayer } in options"
                :key="key"
            >
                <button
                    :class="key === selectedOption?.key ? '--active' : ''"
                    @click="setSelectedFilter(key, label)"
                >
                    {{ label }}
                </button>

                <template
                    v-for="{ secondLayerKey, secondLayerLabel, thirdLayer } in secondLayer"
                    :key="secondLayerKey"
                >
                    <button
                        class="second-layer"
                        :class="secondLayerKey === selectedOption.key ? '--active' : ''"
                        @click="setSelectedFilter(secondLayerKey, secondLayerLabel)"
                    >
                        {{ secondLayerLabel }}
                    </button>

                    <button
                        v-for="{ thirdLayerKey, thirdLayerLabel } in thirdLayer"
                        :key="thirdLayerKey"
                        class="third-layer"
                        :class="thirdLayerKey === selectedOption?.key ? '--active' : ''"
                        @click="setSelectedFilter(thirdLayerKey, thirdLayerLabel)"
                    >
                        {{ thirdLayerLabel }}
                    </button>
                </template>
            </template>
        </div>
    </Transition>
</template>

<style scoped>
    @import '~/assets/scss/filter/filter-select.scss';
    @import '~/assets/scss/filter/filter-shared.scss';
</style>
